import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import { MotionModal, ScrollProgressBar, pressable } from "../../../../components/motion";
import { CloseIcon } from "../../../../components/icons";
import MemorandumContent from "./MemorandumContent";

const TITLE_ID = "mou-modal-title";

// The memorandum in a cream, scrollable box. A thin gold bar on top shows how
// far the reader has got, and a soft fade at the bottom hints there is more
// text until they reach the end.
const MemorandumScrollBox = () => {
    const scrollBoxRef = useRef(null);
    const [isAtBottom, setIsAtBottom] = useState(false);

    const { scrollYProgress } = useScroll({ container: scrollBoxRef, layoutEffect: false });

    useMotionValueEvent(scrollYProgress, "change", (progress) => {
        setIsAtBottom(progress > 0.98);
    });

    return (
        <div className="tw:relative tw:overflow-hidden tw:rounded-md tw:border tw:border-[#F1E4C3] tw:bg-[#FFF8E8]">
            <ScrollProgressBar containerRef={scrollBoxRef} />

            <div
                ref={scrollBoxRef}
                className="memorandum custom-scrollbar-mou tw:max-h-[55vh] tw:overflow-y-auto tw:px-4 tw:py-5 tw:pr-3 tw:sm:px-6"
                tabIndex={0}
            >
                <MemorandumContent />
            </div>

            <motion.div
                className="tw:pointer-events-none tw:absolute tw:inset-x-0 tw:bottom-0 tw:h-16 tw:bg-gradient-to-t tw:from-[#FFF8E8] tw:to-transparent"
                animate={{ opacity: isAtBottom ? 0 : 1 }}
                transition={{ duration: 0.25 }}
                aria-hidden="true"
            />
        </div>
    );
};

const MoUModal = ({ show, handleClose, handleContinue }) => {
    const { t } = useTranslation();
    const [hasAgreed, setHasAgreed] = useState(false);

    return (
        <MotionModal show={show} onClose={handleClose} labelledBy={TITLE_ID} panelClassName="border-top-gold">
            <div className="tw:p-5 tw:sm:p-8">
                {/* Header */}
                <div className="tw:mb-5 tw:flex tw:items-center tw:justify-between tw:gap-4">
                    <h2
                        id={TITLE_ID}
                        className="tw:!m-0 tw:!font-libre-caslon-display tw:!text-[22px] tw:!font-normal tw:!text-[#282828] tw:sm:!text-[26px]"
                    >
                        {t("businessRegister.mouModal.title")}
                    </h2>
                    <motion.button
                        type="button"
                        onClick={handleClose}
                        aria-label="Close"
                        className="tw:flex tw:h-9 tw:w-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:transition-colors tw:hover:bg-[#F6F6F6]"
                        whileTap={{ scale: 0.92 }}
                    >
                        <CloseIcon size={14} color="#4E4E4E" />
                    </motion.button>
                </div>

                <MemorandumScrollBox />

                {/* Agreement + continue */}
                <div className="tw:mt-6 tw:flex tw:flex-col tw:items-center tw:gap-4">
                    <label
                        htmlFor="tnc"
                        // `!` because an un-layered global label rule (display: block) beats tw: utilities.
                        className="tw:!m-0 tw:!flex tw:cursor-pointer tw:!items-center tw:!gap-2.5 tw:text-[14px] tw:text-[#282828] noto-sans"
                    >
                        <input
                            id="tnc"
                            type="checkbox"
                            className="tw:h-[18px] tw:w-[18px] tw:shrink-0 tw:cursor-pointer tw:accent-[#DBBE78]"
                            checked={hasAgreed}
                            onChange={() => setHasAgreed(!hasAgreed)}
                        />
                        {t("businessRegister.mouModal.agree")}
                    </label>

                    <motion.button
                        type="button"
                        onClick={handleContinue}
                        disabled={!hasAgreed}
                        className="tw:h-12 tw:w-full tw:rounded tw:bg-[#DBBE78] tw:text-[14px] tw:font-bold tw:text-white tw:transition-opacity tw:disabled:cursor-not-allowed tw:disabled:opacity-50 tw:sm:w-1/2"
                        // Hover/press feedback only while the button can actually be used.
                        whileHover={hasAgreed ? pressable.hover : undefined}
                        whileTap={hasAgreed ? pressable.tap : undefined}
                    >
                        {t("businessRegister.mouModal.button")}
                    </motion.button>
                </div>
            </div>
        </MotionModal>
    );
};

export default MoUModal;
